export type ProjectStatus = 'live' | 'coming-soon';

export interface ProjectImage {
  src: string;
  alt: string;
  width: number;
  height: number;
  variant: 'desktop' | 'phone';
}

export interface BuiltItem {
  title: string;
  detail: string;
}

export interface Project {
  slug: 'likefamily' | 'rbcarcare' | 'raicesypatitas';
  name: string;
  kind: string;
  location: string;
  status: ProjectStatus;
  url?: string;
  urlLabel?: string;
  summary: string;
  blurb: string;
  overview: string;
  built: BuiltItem[];
  stack: string[];
  tags: string[];
  languages: string;
  statusNote: string;
  logo?: { src: string; alt: string };
  images: {
    card: ProjectImage;
    desktop: ProjectImage;
    mobile: ProjectImage;
    gallery?: ProjectImage[];
  };
  galleryCaption?: string;
}

export const projects: Project[] = [
  {
    slug: 'likefamily',
    name: 'Like Family',
    kind: 'Caregiving services platform',
    location: 'Puerto Rico',
    status: 'live',
    url: 'https://likefamilypr.com',
    urlLabel: 'likefamilypr.com',
    summary: 'Production caregiving platform: public site, admin dashboard, portals, and APIs.',
    blurb:
      'Spanish-language caregiving services platform for Puerto Rico: public site, admin dashboard, caregiver and client portals, ticketing with SLA escalation, document management, and the REST APIs behind them. In production and maintained under an ongoing engagement.',
    overview:
      'End-to-end caregiving services management platform serving Puerto Rico. We design, build, secure, and maintain the public marketing site, the admin dashboard, the caregiver and client portals, the REST APIs, and the infrastructure that ties them all together.',
    built: [
      {
        title: 'Personalized website',
        detail:
          'Custom Spanish landing site with SEO sitemap and rate-limited public quote and contact forms protected by Cloudflare Turnstile.',
      },
      {
        title: 'Admin & client portals',
        detail:
          'Admin dashboard with metrics, charts and global search, plus separate caregiver and client portals with tutors, patients, and service history.',
      },
      {
        title: 'Mobile-ready backend',
        detail: 'Versioned REST APIs (v1 and v2) plus a dedicated caregiver API ready to power native clients.',
      },
      {
        title: 'Ticketing & workflows',
        detail:
          'Group-assigned tickets with SLA escalation, attachments, workflow processes, and full activity history.',
      },
      {
        title: 'Document management',
        detail:
          'Multi-document uploads across many caregiver certification types, expiration alerts, and automatic ID-card renewal.',
      },
      {
        title: 'Cybersecurity',
        detail:
          'Supabase magic-link auth, Laravel Passport & Sanctum, role-based permissions, Cloudflare Turnstile, social sign-in (Apple), login tracking, and a system-wide activity audit log.',
      },
      {
        title: 'Cloud & operations',
        detail:
          'Supabase storage, SMTP delivery with retry & timeout handling, Laravel Mix build pipeline, and StyleCI integration.',
      },
    ],
    stack: [
      'PHP 8.x',
      'Laravel 8',
      'Statamic 3',
      'Supabase',
      'Bootstrap 5',
      'Chart.js',
      'FullCalendar',
      'FilePond',
      'DataTables',
      'PDFMake',
      'Cloudflare Turnstile',
      'Office 365 SMTP',
    ],
    tags: ['Web platform', 'Admin & portals', 'REST API', 'Cybersecurity', 'Spanish UI'],
    languages: 'Spanish',
    statusNote:
      'In production. We continue to ship features, security updates, and maintenance under an ongoing engagement.',
    logo: { src: '/images/projects/likefamily/logo.jpg', alt: 'Like Family logo' },
    images: {
      card: {
        src: '/images/projects/likefamily/card.webp',
        alt: 'Like Family: public website home page (desktop)',
        width: 1200,
        height: 750,
        variant: 'desktop',
      },
      desktop: {
        src: '/images/projects/likefamily/home-desktop.webp',
        alt: 'Like Family: public website home page with service area and request-service call to action (desktop)',
        width: 1440,
        height: 900,
        variant: 'desktop',
      },
      mobile: {
        src: '/images/projects/likefamily/home-mobile.webp',
        alt: 'Like Family: public website home page (mobile)',
        width: 780,
        height: 1688,
        variant: 'phone',
      },
    },
    galleryCaption: 'Screens from the public site at likefamilypr.com. Portals and admin tools sit behind login.',
  },
  {
    slug: 'rbcarcare',
    name: 'RB Car Care',
    kind: 'Local marketing site',
    location: 'Hormigueros, Puerto Rico',
    status: 'live',
    url: 'https://rbcarcare.vercel.app',
    urlLabel: 'rbcarcare.vercel.app',
    summary: 'Appointment-first site for an auto detailing, PPF, and ceramic coating studio.',
    blurb:
      "Appointment-first marketing site for an auto detailing, PPF, and ceramic coating studio in Hormigueros, Puerto Rico. Cinematic dark design in Spanish, mobile-first, built on Next.js with the client's own photography, local SEO, and full icon/Open Graph setup.",
    overview:
      "RB Car Care works by appointment only, so the site has one job: make the studio's paint protection and correction work look as good as it is, then get the visitor to book. We designed a cinematic dark brand system around the client's own photography and built it mobile-first in Spanish.",
    built: [
      { title: 'Brand system', detail: 'Custom dark visual system built around the client’s own vehicle photography.' },
      {
        title: 'Site structure',
        detail: 'Services, work, about, and contact sections, with an FAQ that answers pricing and timing questions up front.',
      },
      { title: 'Conversion', detail: 'Appointment call to action throughout, plus an hours and location block.' },
      {
        title: 'Local SEO & sharing',
        detail: 'Local SEO metadata, favicons, and Open Graph images so links look right when shared.',
      },
      { title: 'Performance', detail: 'Mobile-first layout with responsive imagery generated from the client’s photos.' },
    ],
    stack: ['Next.js', 'Vercel'],
    tags: ['Marketing site', 'Next.js', 'Spanish', 'Mobile-first', 'Local SEO'],
    languages: 'Spanish',
    statusNote: 'Live on Vercel.',
    images: {
      card: {
        src: '/images/projects/rbcarcare/card.webp',
        alt: 'RB Car Care: home page hero with appointment call to action (desktop)',
        width: 1200,
        height: 750,
        variant: 'desktop',
      },
      desktop: {
        src: '/images/projects/rbcarcare/home-desktop.webp',
        alt: 'RB Car Care: home page hero, “Drive and Protect.” over a vehicle photo (desktop)',
        width: 1440,
        height: 900,
        variant: 'desktop',
      },
      mobile: {
        src: '/images/projects/rbcarcare/home-mobile.webp',
        alt: 'RB Car Care: home page hero with appointment button (mobile)',
        width: 780,
        height: 1688,
        variant: 'phone',
      },
      gallery: [
        {
          src: '/images/projects/rbcarcare/services-desktop.webp',
          alt: 'RB Car Care: services page with PPF, ceramic, correction, and detailing (desktop)',
          width: 1440,
          height: 900,
          variant: 'desktop',
        },
      ],
    },
    galleryCaption: 'Screens from the live site at rbcarcare.vercel.app.',
  },
  {
    slug: 'raicesypatitas',
    name: 'Raíces y Patitas',
    kind: 'Bilingual brand site',
    location: 'Caguas, Puerto Rico',
    status: 'coming-soon',
    summary: 'Bilingual site for a pet grooming, cage-free hotel, and meal-prep business.',
    blurb:
      'Bilingual (ES/EN) site for a pet grooming, cage-free hotel, and meal-prep business in Caguas, Puerto Rico. Warm brand system, booking-first flows, and dedicated sections for grooming, hotel, meal prep, requirements, and FAQ. Built on Next.js; launching soon.',
    overview:
      'Raíces y Patitas offers grooming, a cage-free pet hotel, and prepared meals. We built a warm, family-style brand site that explains each service clearly in Spanish and English and keeps booking one tap away on every page.',
    built: [
      { title: 'Bilingual routing', detail: 'Spanish and English versions with a language toggle on every page.' },
      { title: 'Service pages', detail: 'Dedicated pages for grooming, the cage-free hotel, and meal prep.' },
      {
        title: 'Pricing & requirements',
        detail: 'Pages that explain packages by pet size and what is needed before a stay.',
      },
      { title: 'FAQ', detail: 'Answers to the questions owners ask before booking.' },
      { title: 'Booking flow', detail: 'Booking call to action throughout, with a mobile action bar.' },
      {
        title: 'Trust row',
        detail: 'Cage-free with A/C, natural products, and vaccine protocol surfaced right under the hero.',
      },
    ],
    stack: ['Next.js'],
    tags: ['Brand site', 'Next.js', 'ES/EN', 'Booking flow'],
    languages: 'Spanish & English',
    statusNote: 'Pre-launch. The site is built and launching soon; there is no public URL yet.',
    images: {
      card: {
        src: '/images/projects/raicesypatitas/card.webp',
        alt: 'Raíces y Patitas: Spanish home page hero for grooming, cage-free hotel, and meal prep (desktop)',
        width: 1200,
        height: 750,
        variant: 'desktop',
      },
      desktop: {
        src: '/images/projects/raicesypatitas/home-desktop.webp',
        alt: 'Raíces y Patitas: Spanish home page hero with service shortcuts and trust row (desktop)',
        width: 1280,
        height: 800,
        variant: 'desktop',
      },
      mobile: {
        src: '/images/projects/raicesypatitas/home-mobile.webp',
        alt: 'Raíces y Patitas: Spanish home page hero (mobile)',
        width: 780,
        height: 1540,
        variant: 'phone',
      },
      gallery: [
        {
          src: '/images/projects/raicesypatitas/grooming-desktop.webp',
          alt: 'Raíces y Patitas: grooming page hero (desktop)',
          width: 1280,
          height: 800,
          variant: 'desktop',
        },
        {
          src: '/images/projects/raicesypatitas/hotel-desktop.webp',
          alt: 'Raíces y Patitas: cage-free pet hotel page hero (desktop)',
          width: 1280,
          height: 800,
          variant: 'desktop',
        },
      ],
    },
    galleryCaption: 'Pre-launch build. Screens shown are from the development build.',
  },
];

export const getProject = (slug: Project['slug']): Project => {
  const project = projects.find((p) => p.slug === slug);
  if (!project) throw new Error(`Unknown project: ${slug}`);
  return project;
};
